const WEATHER_API_KEY = '555eb1488376452caef233100262005';
const WEATHER_API_URL = 'https://api.weatherapi.com/v1/current.json';

const STORES = [
    {
        name: 'Fig Bar Ambleside',
        city: 'West Vancouver',
        region: 'Vancouver',
        code: 'BC',
        countryCode: 'CA',
        siteId: '493689',
        latitude: 49.327176458847774,
        longitude: -123.15259678834332,
    },
    {
        name: 'Fig Bar Kitsilano',
        city: 'Vancouver',
        region: 'Vancouver',
        code: 'BC',
        countryCode: 'CA',
        siteId: '493689',
        latitude: 49.26808101957894,
        longitude: -123.15155445582164,
    },
    {
        name: 'Fig Bar Mount Pleasant',
        city: 'Vancouver',
        region: 'Vancouver',
        code: 'BC',
        countryCode: 'CA',
        siteId: '493689',
        latitude: 49.25344755890357,
        longitude: -123.10072862883567,
    },
    {
        name: 'Fig Bar Ossington',
        city: 'Toronto',
        region: 'Toronto',
        code: 'ON',
        countryCode: 'CA',
        siteId: '5726529',
        latitude: 43.64548521859742,
        longitude: -79.41945485582167,
    },
    {
        name: 'Fig Bar Summerhill',
        city: 'Toronto',
        region: 'Toronto',
        code: 'ON',
        countryCode: 'CA',
        siteId: '5726529',
        latitude: 43.682822838299124,
        longitude: -79.39205848650701,
    },
    {
        name: 'Fig Bar Midtown',
        city: 'Toronto',
        region: 'Toronto',
        code: 'ON',
        countryCode: 'CA',
        siteId: '5726529',
        latitude: 43.712879011419524,
        longitude: -79.39939895767134,
    },
    {
        name: 'Fig Bar WeHo',
        city: 'Los Angeles',
        region: 'Los Angeles',
        code: 'LA',
        countryCode: 'US',
        siteId: '5729211',
        latitude: 34.08802437969756,
        longitude: -118.34247995767134,
    },
    {
        name: 'Fig Bar Culver Steps',
        city: 'Culver City',
        region: 'Los Angeles',
        code: 'LA',
        countryCode: 'US',
        siteId: '5729211',
        latitude: 34.02677794243555,
        longitude: -118.39398422741411,
    },
];

const FALLBACK_STORE = /** @type {typeof STORES[number]} */ (
    STORES.find((store) => store.name === 'Fig Bar WeHo') || STORES[0]
);

const selectors = {
    store: '[data-geo-store]',
    city: '[data-geo-city]',
    time: '[data-geo-time]',
    condition: '[data-geo-condition]',
    feelsLike: '[data-geo-feels-like]',
};

const REGION_STORAGE_KEY = 'ff_user_region';

const RESOLUTION_CACHE_KEY = 'ff_geo_resolution';

function getDistanceInKm(fromLatitude, fromLongitude, toLatitude, toLongitude) {
    const earthRadiusKm = 6371;
    const latitudeDistance = toRadians(toLatitude - fromLatitude);
    const longitudeDistance = toRadians(toLongitude - fromLongitude);
    const startLatitude = toRadians(fromLatitude);
    const endLatitude = toRadians(toLatitude);

    const haversine =
        Math.sin(latitudeDistance / 2) ** 2 +
        Math.cos(startLatitude) * Math.cos(endLatitude) * Math.sin(longitudeDistance / 2) ** 2;

    return 2 * earthRadiusKm * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

function toRadians(value) {
    return (value * Math.PI) / 180;
}

/**
 * @param {typeof STORES} stores - The candidate stores to search.
 * @param {number} latitude - The latitude to measure distance from.
 * @param {number} longitude - The longitude to measure distance from.
 * @returns {typeof STORES[number]} The closest store in the list.
 */
function findNearestStore(stores, latitude, longitude) {
    return stores.reduce((nearestStore, store) => {
        const nearestDistance = getDistanceInKm(
            latitude,
            longitude,
            nearestStore.latitude,
            nearestStore.longitude
        );
        const storeDistance = getDistanceInKm(latitude, longitude, store.latitude, store.longitude);

        return storeDistance < nearestDistance ? store : nearestStore;
    }, stores[0] || FALLBACK_STORE);
}

function pickStore(latitude, longitude, countryCode) {
    const storesInCountry = countryCode
        ? STORES.filter((store) => store.countryCode === countryCode)
        : [];
    const candidateStores = storesInCountry.length ? storesInCountry : STORES;

    return findNearestStore(candidateStores, latitude, longitude);
}

/**
 * @param {string} [countryName] - The free-text country name from WeatherAPI's location data.
 * @returns {string | undefined} The normalized ISO country code, if recognized.
 */
function normalizeCountryCode(countryName = '') {
    const country = countryName.toLowerCase();

    if (country.includes('canada')) return 'CA';
    if (country.includes('united states') || country === 'usa') return 'US';

    return undefined;
}

async function fetchWeather(query, options = {}) {
    const url = new URL(WEATHER_API_URL);
    url.searchParams.set('key', WEATHER_API_KEY);
    url.searchParams.set('q', query);
    url.searchParams.set('aqi', 'no');

    if (options.cache === 'no-store') url.searchParams.set('_', String(Date.now()));

    const response = await fetch(url.toString(), options);

    if (!response.ok) {
        throw new Error(`WeatherAPI request failed with ${response.status}`);
    }

    return response.json();
}

function toCoordinateQuery(store) {
    return `${store.latitude},${store.longitude}`;
}

async function fetchIpLocation() {
    try {
        const weatherData = await fetchWeather('auto:ip', { cache: 'no-store' });
        return weatherData?.location;
    } catch (error) {
        console.error(error);
        return undefined;
    }
}

async function getGrantedPosition() {
    if (!navigator.geolocation || !navigator.permissions?.query) return undefined;

    try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission.state !== 'granted') return undefined;
    } catch (error) {
        return undefined;
    }

    return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
            (position) => resolve(position.coords),
            () => resolve(undefined),
            {
                enableHighAccuracy: false,
                maximumAge: 1000 * 60 * 5,
                timeout: 5000,
            }
        );
    });
}

function readCachedResolution() {
    try {
        const raw = sessionStorage.getItem(RESOLUTION_CACHE_KEY);
        if (!raw) return undefined;

        const cached = JSON.parse(raw);
        const store = STORES.find((candidate) => candidate.name === cached?.storeName);

        return store ? { store, countryCode: cached?.countryCode } : undefined;
    } catch (error) {
        return undefined;
    }
}

function writeCachedResolution(store, countryCode) {
    try {
        sessionStorage.setItem(
            RESOLUTION_CACHE_KEY,
            JSON.stringify({ storeName: store.name, countryCode })
        );
    } catch (error) {
    }
}

function fallbackStore(countryCode) {
    if (!countryCode) return FALLBACK_STORE;

    return STORES.find((store) => store.countryCode === countryCode) || FALLBACK_STORE;
}

function marketCountryCode() {
    const script = document.querySelector('script[data-market-country]');
    const value = script?.dataset.marketCountry?.toUpperCase();

    return value === 'CA' || value === 'US' ? value : undefined;
}

async function resolveStore() {
    const cached = readCachedResolution();

    if (cached) return cached.store;

    const [ipLocation, position] = await Promise.all([fetchIpLocation(), getGrantedPosition()]);
    const countryCode = normalizeCountryCode(ipLocation?.country) || marketCountryCode();
    const coordinates =
        position ||
        (typeof ipLocation?.lat === 'number' && typeof ipLocation?.lon === 'number'
            ? { latitude: ipLocation.lat, longitude: ipLocation.lon }
            : undefined);

    if (!coordinates) return fallbackStore(countryCode);

    const store = pickStore(coordinates.latitude, coordinates.longitude, countryCode);
    writeCachedResolution(store, countryCode);

    return store;
}

function formatStoreTime(localtime) {
    const time = localtime?.split(' ')[1];
    if (!time) return '';

    const [hourValue, minute = '00'] = time.split(':');
    const hour = Number(hourValue);
    const period = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour % 12 || 12;

    return `${displayHour}:${minute}${period}`;
}

function formatCondition(conditionText = '') {
    const condition = conditionText.toLowerCase();

    if (condition.includes('sunny')) return 'Sunny';
    if (condition.includes('clear')) return 'Clear Sky';
    if (condition.includes('cloud') || condition.includes('overcast')) return 'Cloudy';

    return 'Cloudy';
}

function updateAll(selector, value) {
    if (!value) return;

    for (const element of document.querySelectorAll(selector)) {
        element.innerHTML = value;
    }
}

function renderStoreIdentity(store) {
    updateAll(selectors.store, store.name);
    updateAll(selectors.city, store.city);
}

function renderConditions(weatherData) {
    const currentWeather = weatherData?.current;
    const weatherLocation = weatherData?.location;
    const feelsLike = currentWeather?.feelslike_f;

    updateAll(selectors.time, formatStoreTime(weatherLocation?.localtime));
    updateAll(selectors.condition, formatCondition(currentWeather?.condition?.text));
    updateAll(
        selectors.feelsLike,
        feelsLike === undefined
            ? ''
            : `<span class="deg-text">Feels like</span> ${Math.round(feelsLike)}°`
    );
}

function publishRegion(store) {
    try {
        localStorage.setItem(
            REGION_STORAGE_KEY,
            JSON.stringify({
                region: store.region,
                code: store.code,
                siteId: store.siteId,
            })
        );
    } catch (error) {
        console.error(error);
    }

    document.dispatchEvent(new CustomEvent('ff:region', { detail: store.region }));
}

async function initWeather() {
    if (!document.querySelector(Object.values(selectors).join(', '))) return;

    const store = await resolveStore();

    publishRegion(store);
    renderStoreIdentity(store);

    try {
        renderConditions(await fetchWeather(toCoordinateQuery(store)));
    } catch (error) {
        console.error(error);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWeather, { once: true });
} else {
    initWeather();
}
